/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { StatusContext, StatusContextType } from './StatusContext';
import { WsContext, WsContextType } from './WsContext';
import { AiOp, aiRefresh, getAiOps, execControl, getSetting, queueAiProbe, setSetting } from '../data';

type MenuAction = 'args' | 'enqueue' | 'priority' | 'execute' | 'edit';

// Groups without a main button (just subtask list)
const NO_MAIN_BUTTON_GROUPS = new Set(['utility', 'automation']);
const GROUP_FOLD_STATE_SETTING_KEY = '/ai/opSelector/groupFoldState';
const GROUP_FOLD_STATE_SETTING_DESCRIPTION = 'AI operation selector group fold state as JSON map';

interface AiOpGroup {
    key: string;        // group tag (e.g. 'update', 'utility')
    title: string;      // human title from op.group_title
    mainOps: AiOp[];    // ops with main:true (empty for utility/automation)
    subtasks: AiOp[];   // non-main ops sorted by description
    foldedByDefault: boolean;
}

function AiOpSelector() {
    const theme = useTheme() as any;
    const navigate = useNavigate();
    const wsContext = useContext(WsContext) as WsContextType;
    const statusContext = useContext(StatusContext) as StatusContextType;

    const [aiOps, setAiOps] = useState<AiOp[]>([]);
    const [aiOpPendingId, setAiOpPendingId] = useState<string | null>(null);
    const [showArgsModal, setShowArgsModal] = useState(false);
    const [argsOp, setArgsOp] = useState<AiOp | null>(null);
    const [argsData, setArgsData] = useState<Record<string, any>>({});
    const [pmExecState, setPmExecState] = useState<'stopped' | 'run_one' | 'running' | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; op: AiOp } | null>(null);
    const [hoveredMenuKey, setHoveredMenuKey] = useState<string | null>(null);
    const [foldedGroups, setFoldedGroups] = useState<Record<string, boolean>>({});
    const [savedFoldedGroups, setSavedFoldedGroups] = useState<Record<string, boolean> | null>(null);
    const contextMenuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        aiRefresh().then(() => getAiOps()).then((ops) => {
            setAiOps(ops);
        }).catch((e: any) => {
            console.error('AiOpSelector: error fetching ai ops', e);
            statusContext.postError(`Failed to load AI ops: ${e.message}`);
        });
    }, [statusContext]);

    useEffect(() => {
        let cancelled = false;
        getSetting(GROUP_FOLD_STATE_SETTING_KEY)
            .catch(() => '{}')
            .then((raw) => {
                if (cancelled) return;
                try {
                    const parsed = JSON.parse(raw || '{}');
                    const normalized = Object.fromEntries(
                        Object.entries(parsed || {}).map(([key, value]) => [key, value === true])
                    ) as Record<string, boolean>;
                    setSavedFoldedGroups(normalized);
                } catch (error) {
                    console.error('AiOpSelector: failed to parse saved group fold state', error);
                    setSavedFoldedGroups({});
                }
            })
            .catch((error: any) => {
                console.error('AiOpSelector: failed to load group fold state', error);
                setSavedFoldedGroups({});
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!wsContext.ws) return;
        const onPmStats = (msg: any) => {
            if (msg && msg.execState) {
                setPmExecState(msg.execState);
            }
        };
        wsContext.ws.on('pmStats', onPmStats);
        wsContext.ws.emit('refresh', {});
        return () => {
            wsContext.ws.off('pmStats', onPmStats);
        };
    }, [wsContext]);

    const aiOpGroups = useMemo<AiOpGroup[]>(() => {
        // Group ops by their group tag; ungrouped ops fall into 'utility'
        const byGroup = new Map<string, AiOp[]>();
        for (const op of aiOps) {
            const key = op.group || 'utility';
            const list = byGroup.get(key) || [];
            list.push(op);
            byGroup.set(key, list);
        }

        const groups: AiOpGroup[] = [];
        for (const [key, ops] of byGroup.entries()) {
            const titleOp = ops.find(op => op.group_title) || ops[0];
            const title = titleOp?.group_title || key;
            const mainOps = NO_MAIN_BUTTON_GROUPS.has(key)
                ? []
                : ops.filter(op => op.main).sort((a, b) => (a.description || a.id).localeCompare(b.description || b.id));
            const mainOpSet = new Set(mainOps.map(op => op.id));
            const subtasks = ops
                .filter(op => !mainOpSet.has(op.id))
                .sort((a, b) => (a.description || a.id).localeCompare(b.description || b.id));
            groups.push({
                key,
                title,
                mainOps,
                subtasks,
                foldedByDefault: ops.some(op => op.group_folded === true),
            });
        }

        // Sort: groups with main ops first (alphabetically by title), then automation, then utility
        const groupOrder = (g: AiOpGroup) => {
            if (g.key === 'utility') return 2;
            if (g.key === 'automation') return 1;
            return g.mainOps.length > 0 ? 0 : 1;
        };
        groups.sort((a, b) => {
            const diff = groupOrder(a) - groupOrder(b);
            if (diff !== 0) return diff;
            return a.title.localeCompare(b.title);
        });

        return groups;
    }, [aiOps]);

    useEffect(() => {
        setFoldedGroups(() => {
            const next: Record<string, boolean> = {};
            for (const group of aiOpGroups) {
                next[group.key] = savedFoldedGroups && Object.prototype.hasOwnProperty.call(savedFoldedGroups, group.key)
                    ? savedFoldedGroups[group.key]
                    : group.foldedByDefault;
            }
            return next;
        });
    }, [aiOpGroups, savedFoldedGroups]);

    const cloneArgs = useCallback((data: Record<string, any> | undefined) => {
        return JSON.parse(JSON.stringify(data || {}));
    }, []);

    const openArgsEditor = useCallback((op: AiOp) => {
        setArgsOp(op);
        setArgsData(cloneArgs(op.default_args));
        setShowArgsModal(true);
    }, [cloneArgs]);

    const queueWithArgs = useCallback(async (op: AiOp, overrideArgs?: Record<string, any>) => {
        const opArgs = cloneArgs(overrideArgs ?? op.default_args);
        const detail = Object.keys(opArgs || {}).length > 0 ? { args: opArgs } : undefined;
        return await queueAiProbe(op.id, detail);
    }, [cloneArgs]);

    const doEnqueue = useCallback(async (op: AiOp, overrideArgs?: Record<string, any>) => {
        setAiOpPendingId(op.id);
        try {
            const probeId = await queueWithArgs(op, overrideArgs);
            statusContext.postInfo(`enqueue ${op.id} as probe ${probeId}`);
            navigate(-1);
            return probeId;
        } catch (error: any) {
            console.error('AiOpSelector: failed to enqueue ai op', error);
            statusContext.postError(`Failed to enqueue ${op.id}: ${error.message}`);
            return null;
        } finally {
            setAiOpPendingId(null);
        }
    }, [navigate, queueWithArgs, statusContext]);

    const doPriority = useCallback(async (op: AiOp, overrideArgs?: Record<string, any>) => {
        setAiOpPendingId(op.id);
        try {
            const probeId = await queueWithArgs(op, overrideArgs);
            await execControl(`priority ${probeId}`);
            statusContext.postInfo(`queue ${op.id} in jobs as probe ${probeId}`);
            navigate(-1);
            return probeId;
        } catch (error: any) {
            console.error('AiOpSelector: failed to priority queue ai op', error);
            statusContext.postError(`Failed to queue ${op.id} in jobs: ${error.message}`);
            return null;
        } finally {
            setAiOpPendingId(null);
        }
    }, [navigate, queueWithArgs, statusContext]);

    const doExecute = useCallback(async (op: AiOp, overrideArgs?: Record<string, any>) => {
        setAiOpPendingId(op.id);
        try {
            const probeId = await queueWithArgs(op, overrideArgs);
            await execControl(`priority ${probeId}`);
            if (pmExecState !== 'running') {
                await execControl('all');
            }
            statusContext.postInfo(`execute ${op.id} as probe ${probeId}`);
            setShowArgsModal(false);
            navigate('/dashboard/exec-status');
            return probeId;
        } catch (error: any) {
            console.error('AiOpSelector: failed to execute ai op', error);
            statusContext.postError(`Failed to execute ${op.id}: ${error.message}`);
            return null;
        } finally {
            setAiOpPendingId(null);
        }
    }, [navigate, pmExecState, queueWithArgs, statusContext]);

    const handleMenuAction = useCallback(async (action: MenuAction) => {
        if (!contextMenu) return;
        const op = contextMenu.op;
        setContextMenu(null);
        if (action === 'args') {
            openArgsEditor(op);
            return;
        }
        if (action === 'edit') {
            navigate(`/ai/edit_op/${op.id}`);
            return;
        }
        if (action === 'enqueue') {
            await doEnqueue(op);
            return;
        }
        if (action === 'priority') {
            await doPriority(op);
            return;
        }
        if (action === 'execute') {
            await doExecute(op);
        }
    }, [contextMenu, doEnqueue, doExecute, doPriority, navigate, openArgsEditor]);

    const border = theme.colors.border || theme.colors.ctext || '#444';
    const textMuted = theme.colors.textMuted || '#888';

    const clampContextMenuPosition = useCallback((x: number, y: number, menuWidth = 176, menuHeight = 220) => {
        if (typeof window === 'undefined') {
            return { x, y };
        }
        const margin = 8;
        const maxX = Math.max(margin, window.innerWidth - menuWidth - margin);
        const maxY = Math.max(margin, window.innerHeight - menuHeight - margin);
        return {
            x: Math.min(Math.max(margin, x), maxX),
            y: Math.min(Math.max(margin, y), maxY),
        };
    }, []);

    useEffect(() => {
        if (!contextMenu || !contextMenuRef.current) {
            return;
        }
        const rect = contextMenuRef.current.getBoundingClientRect();
        const next = clampContextMenuPosition(contextMenu.x, contextMenu.y, rect.width, rect.height);
        if (next.x !== contextMenu.x || next.y !== contextMenu.y) {
            setContextMenu((prev) => {
                if (!prev) {
                    return prev;
                }
                if (prev.x === next.x && prev.y === next.y) {
                    return prev;
                }
                return { ...prev, x: next.x, y: next.y };
            });
        }
    }, [clampContextMenuPosition, contextMenu]);

    const openContextMenu = useCallback((x: number, y: number, op: AiOp) => {
        // Offset from cursor so the first item does not appear directly under the pointer.
        const next = clampContextMenuPosition(x - 22, y + 10);
        setContextMenu({ x: next.x, y: next.y, op });
    }, [clampContextMenuPosition]);

    const handleTaskClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, op: AiOp) => {
        event.preventDefault();
        event.stopPropagation();
        openContextMenu(event.clientX, event.clientY, op);
    }, [openContextMenu]);

    const handleTaskRightClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, op: AiOp) => {
        event.preventDefault();
        event.stopPropagation();
        openContextMenu(event.clientX, event.clientY, op);
    }, [openContextMenu]);

    const toggleGroupFold = useCallback((groupKey: string) => {
        setFoldedGroups((prev) => {
            const next = {
                ...prev,
                [groupKey]: !prev[groupKey],
            };
            setSavedFoldedGroups(next);
            void setSetting(
                GROUP_FOLD_STATE_SETTING_KEY,
                JSON.stringify(next),
                GROUP_FOLD_STATE_SETTING_DESCRIPTION,
            ).catch((error: any) => {
                console.error('AiOpSelector: failed to persist group fold state', error);
                statusContext.postError(`Failed to save AI op group fold state: ${error.message}`);
            });
            return next;
        });
    }, [statusContext]);

    return (
        <div style={{
            height: '100%',
            overflow: 'auto',
            backgroundColor: theme.colors.bg || '#111',
            color: theme.colors.text || '#fff',
            padding: '20px 24px',
        }}>
            <div style={{ marginBottom: 20 }}>
                <h5 style={{ color: theme.colors.text || '#fff', margin: 0, fontWeight: 600 }}>
                    AI Operation Selector
                </h5>
            </div>

            <div style={{
                columnWidth: 280,
                columnGap: 14,
            }}>
                {aiOpGroups.map((group) => {
                    const canFold = group.mainOps.length > 0 && group.subtasks.length > 0;
                    const isFolded = canFold ? !!foldedGroups[group.key] : false;
                    const visibleSubtasks = isFolded ? [] : group.subtasks;
                    return (
                    <div
                        key={group.key}
                        style={{
                            border: `1px solid ${border}`,
                            borderRadius: 8,
                            padding: 10,
                            backgroundColor: theme.colors.bg2 || '#1b1f24',
                            minWidth: 0,
                            display: 'inline-block',
                            width: '100%',
                            marginBottom: 14,
                            breakInside: 'avoid',
                            verticalAlign: 'top',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                            <div style={{ color: textMuted, fontSize: '0.72em', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {group.title}
                            </div>
                            {canFold && (
                                <button
                                    type="button"
                                    onClick={() => toggleGroupFold(group.key)}
                                    style={{
                                        border: `1px solid ${border}`,
                                        borderRadius: 4,
                                        backgroundColor: 'transparent',
                                        color: textMuted,
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        fontSize: '0.72em',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        fontFamily: 'monospace',
                                    }}
                                    title={isFolded ? 'Show non-root ops' : 'Hide non-root ops'}
                                >
                                    <FontAwesomeIcon icon={isFolded ? faChevronRight : faChevronDown} />
                                    {isFolded ? 'roots' : 'all'}
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {group.mainOps.map((mainOp, idx) => (
                                <button
                                    key={mainOp.id}
                                    disabled={aiOpPendingId !== null}
                                    onClick={(e) => handleTaskClick(e, mainOp)}
                                    onContextMenu={(e) => handleTaskRightClick(e, mainOp)}
                                    style={{
                                        width: '100%',
                                        minWidth: 0,
                                        textAlign: 'center',
                                        borderRadius: 8,
                                        border: `1px solid ${theme.colors.info || theme.colors.ctext || '#66d9ef'}`,
                                        backgroundColor: theme.colors.bg3 || '#22272e',
                                        color: theme.colors.text || '#fff',
                                        padding: '10px 10px',
                                        cursor: aiOpPendingId !== null ? 'wait' : 'pointer',
                                        fontWeight: 600,
                                    }}
                                >
                                    <div style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{mainOp.id}</div>
                                    <div style={{ fontSize: '0.72em', color: textMuted, marginTop: 2 }}>{mainOp.description}</div>
                                </button>
                            ))}
                            {group.mainOps.length > 0 && visibleSubtasks.length > 0 && (
                                <div style={{ height: 1, backgroundColor: border, opacity: 0.4, margin: '0 2px' }} />
                            )}
                            {visibleSubtasks.map((op) => (
                                <button
                                    key={op.id}
                                    disabled={aiOpPendingId !== null}
                                    onClick={(e) => handleTaskClick(e, op)}
                                    onContextMenu={(e) => handleTaskRightClick(e, op)}
                                    style={{
                                        width: '100%',
                                        minWidth: 0,
                                        textAlign: 'left',
                                        borderRadius: 6,
                                        border: `1px solid ${border}`,
                                        backgroundColor: theme.colors.bg || '#161b22',
                                        color: theme.colors.text || '#fff',
                                        padding: '7px 9px',
                                        cursor: aiOpPendingId !== null ? 'wait' : 'pointer',
                                    }}
                                >
                                    <div style={{ fontFamily: 'monospace', fontSize: '0.8em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{op.id}</div>
                                    <div style={{ fontSize: '0.68em', color: textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{op.description}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                )})}
            </div>

            {contextMenu && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 1999,
                            backgroundColor: 'transparent',
                        }}
                        onClick={() => setContextMenu(null)}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            setContextMenu(null);
                        }}
                    />
                    <div
                        ref={contextMenuRef}
                        style={{
                            position: 'fixed',
                            left: contextMenu.x,
                            top: contextMenu.y,
                            zIndex: 2000,
                            minWidth: 160,
                            border: `1px solid ${border}`,
                            borderRadius: 6,
                            backgroundColor: theme.colors.bg2 || '#1b1f24',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                            padding: 4,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        {[
                            { key: 'enqueue', label: 'Enqueue' },
                            { key: 'args', label: 'Args' },
                            { key: 'priority', label: 'Jobs Q' },
                            { key: 'edit', label: 'Edit' },
                            { key: '__sep', label: '' },
                            { key: 'execute', label: 'Execute' },
                        ].map((item) => {
                            if (item.key === '__sep') {
                                return (
                                    <div
                                        key={item.key}
                                        style={{
                                            height: 1,
                                            backgroundColor: border,
                                            opacity: 0.7,
                                            margin: '4px 6px',
                                        }}
                                    />
                                );
                            }
                            return (
                                <button
                                    key={item.key}
                                    onClick={() => handleMenuAction(item.key as MenuAction)}
                                    onMouseEnter={() => setHoveredMenuKey(item.key)}
                                    onMouseLeave={() => setHoveredMenuKey((k) => (k === item.key ? null : k))}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '6px 10px',
                                        borderRadius: 4,
                                        border: 'none',
                                        backgroundColor: hoveredMenuKey === item.key
                                            ? (theme.colors.headerBg || theme.colors.bg3 || '#2a3038')
                                            : 'transparent',
                                        color: hoveredMenuKey === item.key
                                            ? (theme.colors.ctext || theme.colors.text || '#fff')
                                            : (theme.colors.text || '#fff'),
                                        fontFamily: 'monospace',
                                        fontSize: '0.8em',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            <Modal
                show={showArgsModal}
                onHide={() => setShowArgsModal(false)}
                size="lg"
                contentClassName="bg-dark text-light"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{argsOp?.id || 'AI op'} args</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: theme.colors.bg || '#111', color: theme.colors.text || '#fff' }}>
                    <div style={{ color: textMuted, fontSize: '0.8em', marginBottom: 10 }}>
                        Edit default args, then submit using the current selector mode.
                    </div>
                    <JsonEditor
                        data={argsData}
                        theme={githubDarkTheme}
                        maxWidth="100%"
                        onUpdate={({ newData }) => setArgsData((newData || {}) as Record<string, any>)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowArgsModal(false)}>Cancel</Button>
                    <Button
                        variant="primary"
                        disabled={!argsOp || aiOpPendingId !== null}
                        onClick={() => { if (argsOp) doExecute(argsOp, argsData); }}
                    >
                        Submit
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default AiOpSelector;
